import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Combobox,
  Datepicker,
  TableFilterState,
} from '@ibsys/kit';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { RecordFilters } from '@/pages/records/model/types';
import { cn, getShortName } from '@/shared/lib';
import { useConversationsEmployees } from '@/entities/conversations';

type Props = {
  className?: string;
  setActiveFilters: (filters: TableFilterState) => void;
  filters?: TableFilterState;
};

const parseFilters = (tableFilters: TableFilterState): RecordFilters => {
  return tableFilters.reduce<RecordFilters>((acc, item) => {
    if (item.id === 'createdAt') {
      acc.createdAt = item.value as RecordFilters['createdAt'];
    } else if (item.id === 'id') {
      acc.id = item.value as string;
    }
    return acc;
  }, {});
};

const RecordsFilters: FC<Props> = ({ setActiveFilters, className, filters }) => {
  const { t } = useTranslation(['records']);
  const { control, handleSubmit, reset } = useForm<RecordFilters>({
    defaultValues: parseFilters(filters || []),
  });

  const { data: { employees = [] } = {} } = useConversationsEmployees();

  const onSubmit = (data: RecordFilters) => {
    // Filter out empty values
    const cleanedData = Object.entries(data).reduce<TableFilterState>((acc, [key, value]) => {
      if (key === 'id' && value) {
        acc.push({
          id: 'id',
          value,
        });
      } else if (key === 'createdAt' && (value as RecordFilters['createdAt'])?.from) {
        acc.push({
          id: 'createdAt',
          value,
        });
      } else if (key === 'employeeId' && value) {
        acc.push({
          id: 'employeeId',
          value,
        });
      }
      return acc;
    }, []);

    setActiveFilters(cleanedData);
  };

  const onClear = () => {
    reset({});
    setActiveFilters([]);
  };

  const employeesOptions = employees.map((employee) => ({
    value: employee.id,
    label: getShortName(employee.firstName, employee.lastName, employee.middleName),
  }));

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Card className={cn('gap-3', className)}>
        <CardHeader>
          <CardTitle className={'text-xl'}>{t('records:filter.title')}</CardTitle>
        </CardHeader>
        <CardContent className={'flex flex-col gap-3'}>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <Datepicker
              mode={'range'}
              control={control}
              name="createdAt"
              label={t('records:filter.callDate')}
              showTimeInputs
            />
            <Combobox
              control={control}
              name="employeeId"
              label={t('records:filter.employee')}
              options={employeesOptions}
            />
          </div>
        </CardContent>

        <CardFooter className="flex flex-wrap gap-3">
          <Button type="submit">{t('records:filter.apply')}</Button>
          <Button type="button" variant="secondary" onClick={onClear}>
            {t('records:filter.clear')}
          </Button>
        </CardFooter>
      </Card>
    </form>
  );
};
export default RecordsFilters;
